import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Clock, Home, Mail, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

import { resolveRecorrido } from '@/data/recorridos/config';
import { getItinerarioStops } from '@/data/recorridos/itinerarios';
import { paraderoPath } from '@/lib/paraderoRoutes';
import { getStoredRecorridoId, setStoredRecorridoId } from '@/lib/recorridoStorage';
import { API_BASE as API } from '@/config/api';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { trackVerItinerario } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import HorariosSalida from '@/components/custom/HorariosSalida';

const POLL_MS = 20000;

export default function ItinerarioView() {
  const [searchParams] = useSearchParams();
  const recorridoParam = searchParams.get('recorrido');

  const activeRecorrido = useMemo(
    () => resolveRecorrido(recorridoParam, getStoredRecorridoId()),
    [recorridoParam]
  );

  const isT1 = activeRecorrido.id === 't1a' || activeRecorrido.id === 't1b';

  const [live, setLive] = useState(null);
  const [liveUpdatedAt, setLiveUpdatedAt] = useState(null);
  const [liveError, setLiveError] = useState(null);

  useDocumentTitle(`Itinerario ${activeRecorrido.nombre}`);

  useEffect(() => {
    setStoredRecorridoId(activeRecorrido.id);
  }, [activeRecorrido.id]);

  useEffect(() => {
    trackVerItinerario({ recorridoId: activeRecorrido.id });
  }, [activeRecorrido.id]);

  useEffect(() => {
    setLive(null);
    setLiveUpdatedAt(null);
    setLiveError(null);
  }, [activeRecorrido.id]);

  /** Catálogo estático si no hay datos en vivo (p. ej. sin T1 o API caída). Siempre ida como referencia. */
  const stops = useMemo(
    () => getItinerarioStops(activeRecorrido.id, 'ida'),
    [activeRecorrido.id]
  );

  const fetchItinerarioLive = useCallback(async () => {
    if (!isT1) {
      setLive(null);
      setLiveError(null);
      return;
    }
    try {
      setLiveError(null);
      const { data } = await axios.get(`${API}/trayectos/${activeRecorrido.id}/itinerario-en-vivo`, {
        params: { _t: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      setLive(data);
      setLiveUpdatedAt(new Date());
    } catch (err) {
      setLive(null);
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const detailStr =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => (typeof d === 'string' ? d : d?.msg || '')).filter(Boolean).join(' ')
            : null;
      if (status === 404) {
        setLiveError(
          detailStr ||
            'La API respondió 404. Comprueba que el backend esté desplegado con el endpoint de itinerario en vivo y que en Vercel exista MUNIBUS_BACKEND_URL (proxy) o REACT_APP_BACKEND_URL (build).'
        );
        return;
      }
      if (status === 503) {
        setLiveError(
          detailStr ||
            'El proxy /api no está configurado. En Vercel define la variable MUNIBUS_BACKEND_URL con la URL pública del backend.'
        );
        return;
      }
      setLiveError(detailStr || 'No se pudo cargar el itinerario en vivo.');
    }
  }, [activeRecorrido.id, isT1]);

  useEffect(() => {
    fetchItinerarioLive();
    if (!isT1) return undefined;
    const id = setInterval(fetchItinerarioLive, POLL_MS);
    return () => clearInterval(id);
  }, [fetchItinerarioLive, isT1]);

  const showLiveList = Boolean(isT1 && live?.ok && live.paradas?.length > 0);
  const detected = live?.sentidoDetectado;
  const labelSentido = (s) => (s === 'ida' ? 'Ida' : s === 'vuelta' ? 'Regreso' : '—');

  const rows = useMemo(() => {
    if (showLiveList) {
      return live.paradas.map((p, index) => ({
        key: p.id,
        nombre: p.nombre,
        estado: p.estado,
        etaMin: p.etaMin,
        esTerminal: Boolean(p.esTerminal),
        index,
        isFirst: index === 0,
        isLast: index === live.paradas.length - 1,
        live: true,
      }));
    }
    return stops.map((nombre, index) => ({
      key: `${nombre}-${index}`,
      nombre,
      estado: null,
      etaMin: null,
      esTerminal: false,
      index,
      isFirst: index === 0,
      isLast: index === stops.length - 1,
      live: false,
    }));
  }, [showLiveList, live, stops]);

  const count = rows.length;

  return (
    <div className="min-h-screen bg-background pb-32">
      <header className="glass-header fixed left-0 right-0 top-0 z-50 border-b border-white/40">
        <div className="mx-auto flex h-[4.5rem] w-full max-w-md items-center justify-between px-4">
          <Link
            to={paraderoPath({ recorridoId: activeRecorrido.id })}
            className="flex items-center gap-2 text-foreground"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden />
            <span className="text-sm font-semibold">Volver</span>
          </Link>
          <h1 className="font-headline text-lg font-extrabold text-primary">Itinerario</h1>
          <div className="w-12" />
        </div>
      </header>

      <main className="mx-auto max-w-md px-4 pt-[5.5rem]">
        <section className="mb-4 rounded-2xl border border-border/50 bg-card p-4 shadow-card">
          <p className="text-[11px] font-extrabold uppercase tracking-widest text-muted-foreground">
            Recorrido seleccionado
          </p>
          <h2 className="mt-1 font-headline text-2xl font-black tracking-tight text-foreground">
            {activeRecorrido.nombre}
          </h2>
          {isT1 && detected && (
            <p className="mt-3 text-sm text-foreground">
              <span className="font-semibold text-muted-foreground">Trayecto actual (GPS): </span>
              <span className="font-bold text-primary">{labelSentido(detected)}</span>
            </p>
          )}
        </section>

        <div className="mb-4">
          <HorariosSalida recorrido={activeRecorrido} />
        </div>

        {isT1 && liveError && (
          <p className="mb-3 text-sm text-muted-foreground">{liveError}</p>
        )}

        {isT1 && live?.ok && live?.reason === 'esperando_proxima_salida' && (
          <div
            className="mb-4 rounded-xl border border-primary/25 bg-primary/5 px-3 py-3 text-sm text-foreground"
            role="status"
          >
            <p className="font-semibold text-primary">Recorrido completado</p>
            <p className="mt-1 text-muted-foreground">
              El bus llegó al final de este sentido. Itinerario reiniciado en espera de la próxima salida.
            </p>
          </div>
        )}

        {count === 0 ? (
          <section className="rounded-2xl border border-border/50 bg-card p-5 text-sm text-muted-foreground shadow-card">
            Aún no hay paradas cargadas para este recorrido. Comparte el itinerario de esta línea y lo
            agregamos.
          </section>
        ) : (
          <section className="rounded-2xl border border-border/50 bg-card p-4 shadow-card">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-headline text-lg font-bold text-foreground">Paradas ({count})</h3>
              {showLiveList && detected && (
                <span className="rounded-full bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">
                  {labelSentido(detected)}
                </span>
              )}
            </div>

            {showLiveList && live?.reason !== 'esperando_proxima_salida' && (
              <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
                Tiempos estimados según posición y velocidad del bus; no son horarios oficiales.
                {liveUpdatedAt && (
                  <>
                    {' '}
                    Actualizado {formatDistanceToNow(liveUpdatedAt, { addSuffix: true, locale: es })}.
                  </>
                )}
              </p>
            )}
            {showLiveList && live?.reason === 'esperando_proxima_salida' && liveUpdatedAt && (
              <p className="mb-3 text-xs text-muted-foreground">
                Actualizado {formatDistanceToNow(liveUpdatedAt, { addSuffix: true, locale: es })}.
              </p>
            )}

            <ol className="space-y-2">
              {rows.map((row) => {
                const pasada = row.live && row.estado === 'pasada';
                const pendiente = row.live && row.estado === 'pendiente';
                return (
                  <li
                    key={row.key}
                    className={cn(
                      'flex items-start gap-3 rounded-xl px-2 py-2 transition-colors',
                      pasada ? 'bg-muted/30 opacity-90' : 'hover:bg-muted/40'
                    )}
                  >
                    <span
                      className={cn(
                        'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                        pasada
                          ? 'bg-muted text-muted-foreground'
                          : row.isFirst
                            ? 'bg-primary text-primary-foreground'
                            : row.isLast
                              ? 'bg-secondary text-secondary-foreground'
                              : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {row.index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p
                        className={cn(
                          'font-medium leading-snug text-foreground',
                          pasada && 'text-muted-foreground line-through decoration-muted-foreground/80'
                        )}
                      >
                        {row.nombre}
                      </p>
                      {row.isFirst && !pasada && (
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Inicio</p>
                      )}
                      {row.isLast && !pasada && (
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-secondary">
                          Destino
                        </p>
                      )}
                      {pasada && (
                        <p className="mt-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden />
                          Pasada
                        </p>
                      )}
                      {pendiente && row.esTerminal && (
                        <p className="mt-1 text-sm font-medium leading-snug text-muted-foreground">
                          Cabecera terminal — esperando próxima salida
                        </p>
                      )}
                      {pendiente && row.etaMin != null && !row.esTerminal && (
                        <p className="mt-1 flex items-center gap-1.5 text-sm font-bold text-primary">
                          <Clock className="h-4 w-4 shrink-0" aria-hidden />
                          ~{row.etaMin} min
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        <div className="pb-2 pt-5 text-center">
          <a
            href="https://www.paine.cl/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center rounded-xl p-2 transition-opacity hover:opacity-85"
            aria-label="Visitar sitio Municipalidad de Paine"
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/img/LogoPaine.svg`}
              alt="Municipalidad de Paine"
              className="h-10 w-auto"
            />
          </a>
        </div>
      </main>

      <nav
        className="glass-header fixed bottom-0 left-0 right-0 z-40 rounded-t-3xl border-t border-white/50 shadow-nav-up pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2"
        aria-label="Accesos rápidos"
      >
        <div className="mx-auto flex h-[4.5rem] max-w-md items-center justify-around px-8">
          <Link
            to="/"
            className="flex flex-col items-center gap-1 text-muted-foreground transition-colors hover:text-primary"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
              <Home className="h-5 w-5" aria-hidden />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest">Inicio</span>
          </Link>
          <Link
            to={paraderoPath({ recorridoId: activeRecorrido.id })}
            className="flex flex-col items-center gap-1 text-muted-foreground transition-colors hover:text-primary"
            aria-label="Volver al recorrido en vivo"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full transition-transform active:scale-90">
              <RefreshCw className="h-6 w-6" />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest">Recorrido</span>
          </Link>
          <a
            href="mailto:contacto@paine.cl?subject=Consulta%20itinerario%20MuniBus"
            className="flex flex-col items-center gap-1 text-muted-foreground transition-colors hover:text-primary"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full transition-transform active:scale-90">
              <Mail className="h-6 w-6" aria-hidden />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest">Reportar</span>
          </a>
        </div>
      </nav>
    </div>
  );
}
